import type { NetworkType, ParamTreeNode } from '@mls-wallet/shared-types';
import { HighLevelSolidityType } from '@mls-wallet/shared-types';
import { RawValueAddress } from './RawValueAddress';
import { RawValueList } from './RawValueList';
import { RawValueNumber } from './RawValueNumber';
import { RawValueObject } from './RawValueObject';
import { Alert, AlertDescription } from '@/components/ui/alert';
import type { AddressBookExactEntry } from '@/types/address-book';

/**
 * Route to appropriate raw-value display component based on high-level type
 */
export function RawValue(props: {
  paramTreeNode: ParamTreeNode;
  networkId: NetworkType;
  contractAddress?: string | null;
  inListOrObject: boolean;
  depth: number;
  methodName?: string;
  paramName?: string;
  addressBookEntryByAddress?: Map<string, AddressBookExactEntry>;
}) {
  // Case: parameter node is an array
  if (props.paramTreeNode.highLevelType === HighLevelSolidityType.ARRAY)
    return (
      <RawValueList
        paramTreeNode={props.paramTreeNode}
        networkId={props.networkId}
        inListOrObject={props.inListOrObject}
        depth={props.depth}
        {...(props.paramName ? { paramName: props.paramName } : {})}
        {...(props.methodName ? { methodName: props.methodName } : {})}
        {...(props.addressBookEntryByAddress
          ? { addressBookEntryByAddress: props.addressBookEntryByAddress }
          : {})}
      />
    );

  // Case: parameter node is a struct or tuple
  if (
    props.paramTreeNode.highLevelType === HighLevelSolidityType.STRUCT_OR_TUPLE
  )
    return (
      <RawValueObject
        paramTreeNode={props.paramTreeNode}
        networkId={props.networkId}
        {...(props.contractAddress
          ? { contractAddress: props.contractAddress }
          : {})}
        inListOrObject={props.inListOrObject}
        depth={props.depth}
        {...(props.paramName ? { paramName: props.paramName } : {})}
        {...(props.methodName ? { methodName: props.methodName } : {})}
        {...(props.addressBookEntryByAddress
          ? { addressBookEntryByAddress: props.addressBookEntryByAddress }
          : {})}
      />
    );

  // Case: parameter node is a number
  if (props.paramTreeNode.highLevelType === HighLevelSolidityType.NUMBER)
    return (
      <RawValueNumber
        paramTreeNode={props.paramTreeNode}
        inListOrObject={props.inListOrObject}
        depth={props.depth}
        {...(props.paramName ? { paramName: props.paramName } : {})}
        {...(props.methodName ? { methodName: props.methodName } : {})}
        networkId={props.networkId}
      />
    );

  // Case: parameter node is an address
  if (props.paramTreeNode.highLevelType === HighLevelSolidityType.ADDRESS)
    return (
      <RawValueAddress
        inListOrObject={props.inListOrObject}
        paramTreeNode={props.paramTreeNode}
        depth={props.depth}
        networkId={props.networkId}
        {...(props.addressBookEntryByAddress
          ? { addressBookEntryByAddress: props.addressBookEntryByAddress }
          : {})}
      />
    );

  // Case: parameter node is some misc. type (string, bool, bytes)
  if (props.inListOrObject && props.depth > 1)
    return (
      <div className="font-mono break-all text-sm">
        {props.paramTreeNode.rawValue as string}
      </div>
    );

  // Case: root level misc type, wrap with border
  return (
    <div className="px-3 py-2 border border-zinc-200 border-l-2 border-l-zinc-900 bg-zinc-50 rounded-sm">
      {(() => {
        const rawValue = props.paramTreeNode.rawValue;

        // Case: rawValue is a string
        if (typeof rawValue === 'string')
          return <div className="font-mono break-all text-sm">{rawValue}</div>;

        // Case: rawValue cannot be decoded
        if (rawValue == null) {
          return (
            <Alert variant="destructive">
              <AlertDescription>Value unable to be decoded</AlertDescription>
            </Alert>
          );
        } else {
          return (
            <pre className="font-mono break-all text-sm whitespace-pre-wrap">
              {JSON.stringify(rawValue, null, 2)}
            </pre>
          );
        }
      })()}
    </div>
  );
}
